import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import NavTime from "./components/NavTime";
import ManageRoomTab from "./newTabs/ManageRoomTab";
import GeneralOpeningHoursTab from "./newTabs/GeneralOpeningHoursTab";
import OpeningHoursTab from "./newTabs/OpeningHoursTab";
import DentistAbsentDateTab from "./newTabs/DentistAbsentDateTab";
import DentistGeneralHoursTab from "./newTabs/DentistGeneralHoursTab";
import { ConfirmationModal } from "../bookui/components/ConfirmationModal";
import AppointmentSlotDurationSetting from "./components/AppointmentSlotDurationSetting";
import baseUrl from "../../../api/baseUrl";
import { isRoomAllocationEnabled } from "../../appointment/viewappointment/components/appointmentViewShared";

type ScheduleTab =
  | "manage_room"
  | "general_opening"
  | "opening"
  | "dentist_general"
  | "dentist_absent";

// Every clinic sets its schedule as sessions; Manage Room only shows with
// room allocation.
const SCHEDULE_TABS: { key: ScheduleTab; label: string; roomsOnly?: boolean }[] = [
  { key: "manage_room", label: "Manage Room", roomsOnly: true },
  { key: "general_opening", label: "General Opening hours" },
  { key: "opening", label: "Closing Hours" },
  { key: "dentist_general", label: "Dentist General hours" },
  { key: "dentist_absent", label: "Dentist absent date" },
];

function Managetime() {
  const roomAllocationEnabled = isRoomAllocationEnabled();

  const [formData, setformData] = useState({
    from: "09:00",
    to: "17:00",
    timeDuration: "30",
  });

  const [activeTab, setActiveTab] = useState<ScheduleTab>(
    roomAllocationEnabled ? "manage_room" : "general_opening"
  );
  const [isTimeModalOpen, setIsTimeModalOpen] = useState(false);
  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  const visibleTabs = SCHEDULE_TABS.filter(
    (t) => !t.roomsOnly || roomAllocationEnabled
  );

  const handleResetAll = useCallback(async () => {
    if (isResetting) return;

    setIsResetting(true);
    try {
      const res = await baseUrl.post("/client/setting/manageTime/reset");
      toast.success(res.data?.message || "All schedule data has been reset");
      setResetKey((k) => k + 1);
      setIsResetConfirmOpen(false);
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { message?: string; error?: string } };
      };
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Failed to reset schedule data";
      toast.error(msg);
    } finally {
      setIsResetting(false);
    }
  }, [isResetting]);

  useEffect(() => {
    baseUrl
      .get("/clinic/getTimeData")
      .then((response) => {
        if (response.data.data.startTime) {
          setformData((prevData: any) => ({
            ...prevData,
            from: response.data.data.startTime,
            to: response.data.data.endTime,
          }));
        }
      })
      .catch((error) => {
        console.log(error);
      });
  }, [setformData]);

  return (
    <>
      <div className="mt-2 sm:mt-4 space-y-3 sm:space-y-6 px-2 sm:px-0 text-slate-800 dark:text-slate-200">
        <AppointmentSlotDurationSetting />

        <div className="flex flex-col gap-2 border-b border-slate-200 dark:border-slate-700 p-2">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setIsResetConfirmOpen(true)}
              disabled={isResetting}
              className="px-3 py-1.5 rounded-md text-sm font-semibold border border-red-300 text-red-700 hover:bg-red-50 active:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors outline-none focus-visible:ring-2 focus-visible:ring-red-400/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:border-red-800 dark:text-red-400 dark:hover:bg-red-950/40 dark:active:bg-red-950/60 dark:focus-visible:ring-offset-slate-900"
            >
              Reset all
            </button>
            <button
              type="button"
              onClick={() => setIsTimeModalOpen(true)}
              className="px-3 py-1.5 rounded-md text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 active:bg-slate-700 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200 dark:active:bg-slate-300 dark:focus-visible:ring-offset-slate-900"
            >
              Clinic Hours
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            {visibleTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={[
                  "px-3 py-1.5 rounded-md text-sm font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900 active:scale-[0.98]",
                  activeTab === tab.key
                    ? "bg-primary text-white shadow-sm hover:bg-primary/90 active:bg-primary/80"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200 active:bg-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700 dark:active:bg-slate-600",
                ].join(" ")}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div key={resetKey}>
          {activeTab === "manage_room" && roomAllocationEnabled && (
            <ManageRoomTab />
          )}
          {activeTab === "general_opening" && (
            <GeneralOpeningHoursTab
              clinicSessionStart={formData.from}
              clinicSessionEnd={formData.to}
            />
          )}
          {activeTab === "opening" && <OpeningHoursTab />}
          {activeTab === "dentist_general" && <DentistGeneralHoursTab />}
          {activeTab === "dentist_absent" && <DentistAbsentDateTab />}
        </div>
      </div>
      {isResetConfirmOpen && (
        <ConfirmationModal
          title="Reset all schedule data?"
          message={`This will permanently delete all ${
            roomAllocationEnabled ? "rooms, " : ""
          }general opening hours, closing hours, dentist general hours, and dentist absent dates for this clinic. This action cannot be undone.`}
          confirmText={isResetting ? "Resetting…" : "Reset all"}
          cancelText="Cancel"
          isLoading={isResetting}
          onConfirm={handleResetAll}
          onCancel={() => setIsResetConfirmOpen(false)}
        />
      )}
      {isTimeModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4"
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            onClick={() => setIsTimeModalOpen(false)}
            aria-label="Close modal"
          />
          <div className="relative w-full max-w-sm sm:max-w-md">
            <div className="rounded-xl bg-transparent">
              <NavTime
                setformData={setformData}
                formData={formData}
                onClose={() => setIsTimeModalOpen(false)}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Managetime;
